import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { pickupEventsAPI, deliveriesAPI } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { showToast } from '../components/Toast';
import './PickupEventDetail.css';

function PickupEventDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [event, setEvent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [newMessage, setNewMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [availableDeliveries, setAvailableDeliveries] = useState([]);
  const [selectedDeliveries, setSelectedDeliveries] = useState([]);
  const [linking, setLinking] = useState(false);

  useEffect(() => {
    loadEvent();
    if (user?.role === 'admin') {
      loadAvailableDeliveries();
    }
    // Poll for updates every 5 seconds
    const interval = setInterval(loadEvent, 5000);
    return () => clearInterval(interval);
  }, [id, user]);

  const loadEvent = async () => {
    try {
      const response = await pickupEventsAPI.getById(id);
      setEvent(response.data);
    } catch (error) {
      console.error('Error loading pickup event:', error);
      if (error.response?.status === 403 || error.response?.status === 404) {
        alert('Pickup event not found or you do not have access');
        navigate(-1);
      }
    } finally {
      setLoading(false);
    }
  };

  const loadAvailableDeliveries = async () => {
    try {
      const response = await deliveriesAPI.getAll();
      // Filter out deliveries that don't have a pickup event
      const unlinked = response.data.filter(d => !d.pickup_event_id);
      setAvailableDeliveries(unlinked);
    } catch (error) {
      console.error('Error loading available deliveries:', error);
    }
  };

  const handleLinkDeliveries = async () => {
    if (selectedDeliveries.length === 0) {
      showToast('Please select at least one delivery to link', 'warning');
      return;
    }

    setLinking(true);
    try {
      await pickupEventsAPI.assignDeliveries(id, selectedDeliveries);
      setSelectedDeliveries([]);
      loadEvent();
      loadAvailableDeliveries();
      showToast('Deliveries linked successfully', 'success');
    } catch (error) {
      const errorMessage = error.response?.data?.error || 'Unknown error';
      showToast(`Error linking deliveries: ${errorMessage}`, 'error');
    } finally {
      setLinking(false);
    }
  };

  const handleToggleDelivery = (deliveryId) => {
    setSelectedDeliveries(prev => {
      if (prev.includes(deliveryId)) {
        return prev.filter(id => id !== deliveryId);
      } else {
        return [...prev, deliveryId];
      }
    });
  };

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!newMessage.trim()) return;

    setSending(true);
    try {
      await pickupEventsAPI.addMessage(id, newMessage.trim());
      setNewMessage('');
      loadEvent(); // Reload to show new message
    } catch (error) {
      alert('Error sending message: ' + (error.response?.data?.error || 'Unknown error'));
    } finally {
      setSending(false);
    }
  };

  const handleConfirm = async () => {
    try {
      if (user.role === 'admin') {
        await pickupEventsAPI.confirmAdmin(id);
      } else {
        await pickupEventsAPI.confirmDriver(id);
      }
      loadEvent();
    } catch (error) {
      alert('Error confirming attendance');
    }
  };

  const handleUnconfirm = async () => {
    try {
      if (user.role === 'admin') {
        await pickupEventsAPI.unconfirmAdmin(id);
      } else {
        await pickupEventsAPI.unconfirmDriver(id);
      }
      loadEvent();
    } catch (error) {
      alert('Error unconfirming attendance');
    }
  };

  const formatDateTime = (dateString) => {
    return new Date(dateString).toLocaleString();
  };

  const formatMessageTime = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffMins < 1440) return `${Math.floor(diffMins / 60)}h ago`;
    return date.toLocaleString();
  };

  const getMyConfirmation = () => {
    if (user.role === 'admin') {
      return event.admin_confirmed;
    } else {
      const myAttendance = event.attendances?.find(a => a.driver_id === user.id);
      return myAttendance?.confirmed || false;
    }
  };

  if (loading) return <div className="loading">Loading...</div>;
  if (!event) return <div className="error">Pickup event not found</div>;

  const myConfirmation = getMyConfirmation();

  return (
    <div className="page pickup-event-detail-page">
      <button onClick={() => navigate(-1)} className="btn btn-secondary back-btn">
        ← Back
      </button>

      <div className="detail-header">
        <div>
          <h1>{formatDateTime(event.scheduled_datetime)}</h1>
          <p className="location">{event.location}</p>
        </div>
        <div className="header-actions">
          <div className="pickup-code-display">
            <span className="code-label">Pickup Code</span>
            <span className="code-value">{event.pickup_code}</span>
          </div>
          {myConfirmation ? (
            <button onClick={handleUnconfirm} className="btn btn-warning">
              Unconfirm Attendance
            </button>
          ) : (
            <button onClick={handleConfirm} className="btn btn-success">
              Confirm Attendance
            </button>
          )}
        </div>
      </div>

      <div className="detail-grid">
        <div className="detail-section">
          <div className="card">
            <h2>Event Information</h2>
            <div className="info-grid">
              <div className="info-item">
                <span className="info-label">Status:</span>
                <span className={`status-badge status-${event.status}`}>
                  {event.status}
                </span>
              </div>
              <div className="info-item">
                <span className="info-label">Admin Confirmed:</span>
                <span className={event.admin_confirmed ? 'confirmed' : 'not-confirmed'}>
                  {event.admin_confirmed ? 'Yes ✓' : 'No'}
                </span>
              </div>
              <div className="info-item">
                <span className="info-label">Drivers Attending:</span>
                <span>{event.attendances?.length || 0}</span>
              </div>
              <div className="info-item">
                <span className="info-label">Deliveries Assigned:</span>
                <span>{event.deliveries?.length || 0}</span>
              </div>
            </div>
          </div>

          <div className="card">
            <h2>Products to Transfer</h2>
            {event.product_summary && event.product_summary.length > 0 ? (
              <table className="products-table">
                <thead>
                  <tr>
                    <th>Product</th>
                    <th>Quantity (bags)</th>
                  </tr>
                </thead>
                <tbody>
                  {event.product_summary.map((product) => (
                    <tr key={product.product_id}>
                      <td>{product.name}</td>
                      <td className="quantity">{product.quantity}</td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr>
                    <td><strong>Total Bags:</strong></td>
                    <td className="quantity">
                      <strong>
                        {event.product_summary.reduce((sum, p) => sum + p.quantity, 0)}
                      </strong>
                    </td>
                  </tr>
                </tfoot>
              </table>
            ) : (
              <p className="no-data">No deliveries assigned to this pickup event yet</p>
            )}
          </div>

          <div className="card">
            <h2>Driver Attendance</h2>
            {event.attendances && event.attendances.length > 0 ? (
              <ul className="drivers-list">
                {event.attendances.map((attendance) => (
                  <li key={attendance.id} className="driver-item">
                    <span className="driver-name">{attendance.driver_name}</span>
                    {attendance.confirmed ? (
                      <span className="confirmed-badge">✓ Confirmed</span>
                    ) : (
                      <span className="pending-badge">Pending</span>
                    )}
                  </li>
                ))}
              </ul>
            ) : (
              <p className="no-data">No drivers assigned yet</p>
            )}
          </div>

          {user?.role === 'admin' && availableDeliveries.length > 0 && (
            <div className="card">
              <h2>Link Unassigned Deliveries</h2>
              <p className="card-description">
                Select deliveries to link to this pickup event:
              </p>
              <div className="deliveries-list">
                {availableDeliveries.map((delivery) => (
                  <label key={delivery.id} className="delivery-checkbox-item">
                    <input
                      type="checkbox"
                      checked={selectedDeliveries.includes(delivery.id)}
                      onChange={() => handleToggleDelivery(delivery.id)}
                      disabled={linking}
                    />
                    <span className="delivery-info">
                      <strong>Delivery #{delivery.id}</strong>
                      {delivery.delivery_code && ` - ${delivery.delivery_code}`}
                      <br />
                      <small>Order #{delivery.order_id} - {delivery.status}</small>
                    </span>
                  </label>
                ))}
              </div>
              <button
                onClick={handleLinkDeliveries}
                disabled={linking || selectedDeliveries.length === 0}
                className="btn btn-primary"
                style={{ marginTop: '1rem' }}
              >
                {linking ? 'Linking...' : `Link ${selectedDeliveries.length} Deliveries`}
              </button>
            </div>
          )}
        </div>

        <div className="chat-section">
          <div className="card chat-card">
            <h2>Chat</h2>
            <div className="messages-container">
              {event.messages && event.messages.length > 0 ? (
                event.messages.map((message) => (
                  <div
                    key={message.id}
                    className={`message ${message.user_id === user.id ? 'message-mine' : 'message-theirs'}`}
                  >
                    <div className="message-header">
                      <span className="message-author">
                        {message.user_name}
                        {message.user_role === 'admin' && <span className="role-badge">Admin</span>}
                      </span>
                      <span className="message-time">{formatMessageTime(message.created_at)}</span>
                    </div>
                    <div className="message-content">{message.message}</div>
                  </div>
                ))
              ) : (
                <p className="no-messages">No messages yet. Start the conversation!</p>
              )}
            </div>

            <form onSubmit={handleSendMessage} className="message-form">
              <textarea
                value={newMessage}
                onChange={(e) => setNewMessage(e.target.value)}
                placeholder="Type your message..."
                rows="3"
                disabled={sending}
                className="message-input"
              />
              <button
                type="submit"
                disabled={sending || !newMessage.trim()}
                className="btn btn-primary send-btn"
              >
                {sending ? 'Sending...' : 'Send Message'}
              </button>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
}

export default PickupEventDetail;
